import { useEffect } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useSession } from '@/context/SessionContext';
import gTagConstants from '@/constants/gEvenets.constants';
import useHasMounted from '@/hooks/useHasMounted';
// Declare global types for gtag and dataLayer to avoid TypeScript errors
// These are used for sending analytics events
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export default function FirstVisitEvent() {
  // Get session and analytics helpers from context/hooks
  const hasMounted = useHasMounted();
  const session = useSession();
  const {
    isFirstVisit,
    setFirstVisitCookie,
    utmParams,
    pageParams,
    getDeviceCategory,
    getNextBatchOrderingId,
    getBatchPageId,
    getGASessionId,
    getGASessionNumber,
    skuSlug,
    getPageType,
  } = useAnalytics();

  useEffect(() => {
    // Only run on client side and if this is the user's first visit
    if (typeof window === 'undefined') return;
    if (!isFirstVisit) return;
    if (!hasMounted) return;

    // Use sessionToken as the session_id for analytics
    const sessionId = session.sessionToken;

    // Always send UTM parameters, using 'not_set' as a default value if missing
    // This allows explicit tracking of missing UTM data in GA4 reports
    const params: Record<string, any> = {
      batch_ordering_id: getNextBatchOrderingId(), // Incremental event ordering for the page
      batch_page_id: getBatchPageId(), // Unique page load identifier
      campaign: utmParams.campaign || 'not_set', // UTM campaign name
      campaign_id: utmParams.campaign_id || 'not_set', // UTM campaign ID
      content: utmParams.content || 'not_set', // UTM content
      medium: utmParams.medium || 'not_set', // UTM medium
      source: utmParams.source || 'not_set', // UTM source
      term: utmParams.term || 'not_set', // UTM term
      ga_session_id: getGASessionId(), // GA session ID from cookie
      ga_session_number: getGASessionNumber(), // GA session number from cookie
      ...pageParams, // Page location, referrer, and title
      device_category: getDeviceCategory(), // Device type (mobile/tablet/desktop)
      session_id: sessionId, // Session identifier
      page_type: getPageType(), // Page type
    };
    // If the page is a product page, add the SKU slug
    if (skuSlug) {
      params.sku = skuSlug;
    }

    // Send the event to Google Analytics (gtag or dataLayer)
    if (window.gtag) {
      window.gtag('event', gTagConstants.eventKeys.first_visit, params);
    } else if (window.dataLayer) {
      window.dataLayer.push({ event: gTagConstants.eventKeys.first_visit, ...params });
    }

    // Set the first visit cookie so this event only fires once per user
    setFirstVisitCookie();
  }, [
    isFirstVisit,
    hasMounted,
    session,
    utmParams,
    pageParams,
    getDeviceCategory,
    getNextBatchOrderingId,
    getBatchPageId,
    getGASessionId,
    getGASessionNumber,
    skuSlug,
    setFirstVisitCookie,
    getPageType,
  ]);

  // This component does not render anything in the UI
  return null;
}
